import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import { Box, Button, Grid, Typography } from "@mui/material";
import PropTypes from "prop-types";

import YesNoRadioButton from "Components/RegisterBuildingForm/YesNoRadioButton";
import InputFieldText from "Components/Common/InputFieldText";
import { sprinklerTypeEnum } from "common/enums";
import {
  fireSafetyBasementCollection,
  fireSafetyFloorCollection,
} from "common/constant";
import SelectField from "Components/Common/SelectField";

import { onlyNumberKeys } from "utils";

const AutomaticSprinklerSystem = ({ formik, t, floorOptions }) => {
  const {
    setFieldValue,
    setFieldTouched,
    values: {
      fireProtection: {
        isGroundFloorHasSprinklerSystem,
        isHollowPlinthHasSprinklerSystem,
        isFloorHasSprinklerSystem,
        isBasementHasSprinklerSystem,
        basementSprinklers,
        floorSprinklers,
      },
    },
  } = formik;

  const { filteredBasementOptions, filteredFloorOptions } = useMemo(
    () => ({
      filteredBasementOptions: floorOptions.filter(({ label }) =>
        fireSafetyBasementCollection.includes(label)
      ),
      filteredFloorOptions: floorOptions.filter(({ label }) =>
        fireSafetyFloorCollection.includes(label)
      ),
    }),
    [floorOptions]
  );

  const handleAddSprinkler = (existingSprinklers, isBasement = false) => {
    const newSprinklerSystem = [
      ...existingSprinklers,
      {
        id: 0,
        isBasement: isBasement ? true : false,
        sprinklerType: "",
        floorId: "",
        quantity: "",
      },
    ];
    setFieldValue(
      isBasement
        ? "fireProtection.basementSprinklers"
        : "fireProtection.floorSprinklers",
      newSprinklerSystem
    );
    setFieldTouched(
      isBasement
        ? `fireProtection.basementSprinklers[${newSprinklerSystem.length - 1}]`
        : `fireProtection.floorSprinklers[${newSprinklerSystem.length - 1}]`,
      false,
      false
    );
  };

  const handleRemoveSprinkler = (index, isBasement = false) => {
    isBasement
      ? setFieldValue(
          "fireProtection.basementSprinklers",
          basementSprinklers.filter((_, ind) => ind !== index)
        )
      : setFieldValue(
          "fireProtection.floorSprinklers",
          floorSprinklers.filter((_, ind) => ind !== index)
        );
  };

  useEffect(() => {
    if (!basementSprinklers.length && isBasementHasSprinklerSystem) {
      setFieldValue("fireProtection.basementSprinklers", [
        {
          id: 0,
          isBasement: true,
          sprinklerType: "",
          floorId: "",
          quantity: "",
        },
      ]);
    }
    if (!isBasementHasSprinklerSystem && basementSprinklers.length) {
      setFieldValue("fireProtection.basementSprinklers", []);
    }
  }, [basementSprinklers, isBasementHasSprinklerSystem]);

  useEffect(() => {
    if (!floorSprinklers.length && isFloorHasSprinklerSystem) {
      setFieldValue("fireProtection.floorSprinklers", [
        {
          id: 0,
          isBasement: false,
          sprinklerType: "",
          floorId: "",
          quantity: "",
        },
      ]);
    }
    if (!isFloorHasSprinklerSystem && floorSprinklers.length) {
      setFieldValue("fireProtection.floorSprinklers", []);
    }
  }, [floorSprinklers, isFloorHasSprinklerSystem]);

  return (
    <Grid container columnSpacing={1} rowSpacing={1} sx={{ pl: 1 }}>
      <Grid item xs={12}>
        <Typography>
          {t("Has automatic sprinkler system been provided?")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={2}
          sx={{ pl: 1, minHeight: "70px" }}
        >
          <YesNoRadioButton
            label="Ground Floor"
            keyName="fireProtection.isGroundFloorHasSprinklerSystem"
            formik={formik}
            isRequired
            onChange={(e) => {
              setFieldValue(
                "fireProtection.groundFloorSprinklerSystemQuantity",
                ""
              );
              setFieldValue(
                "fireProtection.isGroundFloorHasSprinklerSystem",
                !!+e.target.value
              );
            }}
          />

          {isGroundFloorHasSprinklerSystem && (
            <Grid item xs={12} sm={12} md={6} lg={4}>
              <InputFieldText
                label={t("Qty") + "*"}
                name="fireProtection.groundFloorSprinklerSystemQuantity"
                formik={formik}
                placeholder="Enter Quantity"
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={2}
          sx={{ pl: 1, minHeight: "70px" }}
        >
          <YesNoRadioButton
            label="Hollow Plinth"
            keyName="fireProtection.isHollowPlinthHasSprinklerSystem"
            formik={formik}
            isRequired
            onChange={(e) => {
              setFieldValue(
                "fireProtection.hollowPlinthSprinklerSystemQuantity",
                ""
              );
              setFieldValue(
                "fireProtection.isHollowPlinthHasSprinklerSystem",
                !!+e.target.value
              );
            }}
          />

          {isHollowPlinthHasSprinklerSystem && (
            <Grid item xs={12} sm={12} md={6} lg={4}>
              <InputFieldText
                label={t("Qty") + "*"}
                name="fireProtection.hollowPlinthSprinklerSystemQuantity"
                formik={formik}
                placeholder="Enter Quantity"
                type="number"
                InputProps={{
                  inputProps: {
                    min: 0,
                    onKeyDown: (e) => onlyNumberKeys(e, true),
                  },
                }}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item className="border ps-4 pe-2 pb-2 pt-2" sx={{ mb: 3 }} xs={12}>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={2}
          sx={{ pl: 1, minHeight: "70px" }}
        >
          <YesNoRadioButton
            label="Basements"
            keyName="fireProtection.isBasementHasSprinklerSystem"
            formik={formik}
            isRequired
            onChange={(e) => {
              setFieldValue(
                "fireProtection.isBasementHasSprinklerSystem",
                !!+e.target.value
              );
            }}
          />

          <Box sx={{ mt: 1 }}>
            <Button
              color="primary"
              sx={{ minWidth: "unset", textTransform: "capitalize" }}
              variant="outlined"
              disabled={
                !isBasementHasSprinklerSystem ||
                !(basementSprinklers.length < filteredBasementOptions.length)
              }
              onClick={() => handleAddSprinkler(basementSprinklers, true)}
            >
              <strong>
                <span>+</span> {t("Add Basement Sprinklers")}
              </strong>
            </Button>
          </Box>

          {!!basementSprinklers.length &&
            basementSprinklers.map(({ quantity }, index) => (
              <Grid
                key={index}
                container
                columnSpacing={1}
                rowSpacing={1}
                className="border p-2 pe-3 mt-2"
              >
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <SelectField
                    label={t("Sprinkler Type") + "*"}
                    name={`fireProtection.basementSprinklers.${index}.sprinklerType`}
                    formik={formik}
                    options={sprinklerTypeEnum}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <SelectField
                    label={t("Basement") + "*"}
                    name={`fireProtection.basementSprinklers.${index}.floorId`}
                    formik={formik}
                    options={filteredBasementOptions}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Quantity") + "*"}
                    name={`fireProtection.basementSprinklers.${index}.quantity`}
                    formik={formik}
                    type="number"
                    value={quantity ?? ""}
                    placeholder="Enter Quantity"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>

                {basementSprinklers.length > 1 && (
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Button
                      color="error"
                      size="small"
                      sx={{
                        minWidth: "unset",
                        border: "1px solid",
                        height: "40px",
                        width: "100px",
                        mb: 2,
                      }}
                      onClick={() => handleRemoveSprinkler(index, true)}
                    >
                      <strong className="d-flex justify-content-center">
                        <DeleteOutlineSharpIcon fontSize="small" />
                        {t("Remove")}
                      </strong>
                    </Button>
                  </Grid>
                )}
              </Grid>
            ))}
        </Grid>
      </Grid>
      <Grid item className="border ps-4 pe-2 pb-2 pt-2" sx={{ mb: 3 }} xs={12}>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={2}
          sx={{ pl: 1, minHeight: "70px" }}
        >
          <YesNoRadioButton
            label="Floors"
            keyName="fireProtection.isFloorHasSprinklerSystem"
            formik={formik}
            isRequired
            onChange={(e) => {
              setFieldValue(
                "fireProtection.isFloorHasSprinklerSystem",
                !!+e.target.value
              );
            }}
          />

          <Box sx={{ mt: 1 }}>
            <Button
              color="primary"
              sx={{ minWidth: "unset", textTransform: "capitalize" }}
              variant="outlined"
              disabled={
                !isFloorHasSprinklerSystem ||
                !(floorSprinklers.length < filteredFloorOptions.length)
              }
              onClick={() => handleAddSprinkler(floorSprinklers)}
            >
              <strong>
                <span>+</span> {t("Add Floor Sprinklers")}
              </strong>
            </Button>
          </Box>

          {!!floorSprinklers.length &&
            floorSprinklers.map(({ quantity }, index) => (
              <Grid
                key={index}
                container
                columnSpacing={1}
                rowSpacing={1}
                className="border p-2 pe-3 mt-2"
              >
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <SelectField
                    label={t("Sprinkler Type") + "*"}
                    name={`fireProtection.floorSprinklers.${index}.sprinklerType`}
                    formik={formik}
                    options={sprinklerTypeEnum}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <SelectField
                    label={t("Floor") + "*"}
                    name={`fireProtection.floorSprinklers.${index}.floorId`}
                    formik={formik}
                    options={filteredFloorOptions}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Quantity") + "*"}
                    name={`fireProtection.floorSprinklers.${index}.quantity`}
                    formik={formik}
                    type="number"
                    value={quantity ?? ""}
                    placeholder="Enter Quantity"
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>

                {floorSprinklers.length > 1 && (
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Button
                      color="error"
                      size="small"
                      sx={{
                        minWidth: "unset",
                        border: "1px solid",
                        height: "40px",
                        width: "100px",
                        mb: 2,
                      }}
                      onClick={() => handleRemoveSprinkler(index)}
                    >
                      <strong className="d-flex justify-content-center">
                        <DeleteOutlineSharpIcon fontSize="small" />
                        {t("Remove")}
                      </strong>
                    </Button>
                  </Grid>
                )}
              </Grid>
            ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

AutomaticSprinklerSystem.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
  floorOptions: PropTypes.array,
};

export default withTranslation()(AutomaticSprinklerSystem);
